import { useState, useCallback, useEffect, useMemo } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  Position,
  Handle,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import * as LucideIcons from "lucide-react";
const {
  RefreshCw,
  AlertCircle,
  Map: MapIcon,
  X,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Copy,
  Check,
  ChevronRight,
  ChevronDown,
  Star,
} = LucideIcons;
import { Link } from "react-router-dom";
import { getSitemapTree, crawlSitemap, getClusters, getArticleProposal, applyLinks, getSitemapPlans } from "../lib/api";
import { gradeColor, AEO_FACTORS, citationGaps } from "../lib/aeo";
import { analyzeSupport, normUrl, pathOf, PILLAR_MIN_LINKS, STRONG_POSTS } from "../lib/sitemapSupport";

// ── Card dimensions ──────────────────────────────────────────────────────────

// One card per URL section, the store and each topic cluster, with its pages/posts as rows
// (Query Map style). One node per page put 400+ nodes in a single dagre column.
const CARD_W = 280;
const HEAD_H = 70;
const ROW_H = 24;
const FOOT_H = 20;
const MAX_ROWS = 8;
const ROW_MAX_W = 2400;
const GAP = 40;
const BAND_H = 40;

// ── Card type config ─────────────────────────────────────────────────────────

const TYPE_CONFIG = {
  section:  { color: "#1D4ED8", bg: "#EFF6FF", border: "#93C5FD" },
  store:    { color: "#92400E", bg: "#FEF9C3", border: "#FCD34D" },
  cluster:  { color: "#0369A1", bg: "#E0F2FE", border: "#38BDF8" },
  category: { color: "#9F1239", bg: "#FFE4E6", border: "#FB7185" },
  plan:     { color: "#0F766E", bg: "#F0FDFA", border: "#14B8A6" },
};

// "With plan" view: stored topic cluster plans drawn as their own cards.
const PLAN_STROKE = "#0D9488";
const PLAN_TIER = { 2: "pillar", 3: "article", 4: "sub-article" };

const GREEN = "#10B981";
const AMBER = "#F59E0B";
const RED = "#F87171";
const OLD = "#B91C1C";
const LEVEL = {
  strong:  { color: GREEN, label: "Fully supported" },
  partial: { color: AMBER, label: "Partly supported" },
  none:    { color: RED, label: "No support" },
};
const LEVEL_ORDER = { none: 0, partial: 1, strong: 2 };

// Above this many pages, Pages splits by first URL segment (/service, /work…):
// without a CMS the URL path is the only hierarchy a site gives us.
const SECTION_MIN_PAGES = 6;

function cfg(type) {
  return TYPE_CONFIG[type] || TYPE_CONFIG.section;
}

// ── Copy button ───────────────────────────────────────────────────────────────

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button onClick={handle} title="Copy URL" className="shrink-0 text-gray-400 hover:text-gray-600">
      {copied ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
    </button>
  );
}

// ── Card node ─────────────────────────────────────────────────────────────────

function cardHeight(card, expanded) {
  const shown = expanded ? card.rows.length : Math.min(card.rows.length, MAX_ROWS);
  const foot = card.rows.length > MAX_ROWS ? FOOT_H : 0;
  return HEAD_H + Math.max(shown, 1) * ROW_H + foot + 8;
}

function CardNode({ data }) {
  const { card, expanded, lit, selectedCard, selectedRow, focus, onRow, onToggle } = data;
  const { color, bg, border } = cfg(card.kind);
  // Rows lit by the selection come first, so a page's supporting posts show without expanding.
  const ordered = focus?.size ? [...card.rows.filter((r) => focus.has(r.key)), ...card.rows.filter((r) => !focus.has(r.key))] : card.rows;
  const rows = expanded ? ordered : ordered.slice(0, MAX_ROWS);
  const hidden = card.rows.length - rows.length;
  const line = card.line;

  return (
    <div
      title={card.label}
      style={{
        width: CARD_W,
        height: cardHeight(card, expanded),
        background: bg,
        border: `1.5px ${card.kind === "plan" ? "dashed" : "solid"} ${border}`,
        borderRadius: 10,
        boxShadow: selectedCard ? `0 0 0 3px ${border}66` : "0 1px 3px rgba(0,0,0,0.08)",
        opacity: lit ? 1 : 0.2,
        transition: "opacity 0.2s",
        overflow: "hidden",
        cursor: "pointer",
      }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <div style={{ padding: "8px 10px 6px", borderBottom: `1px solid ${border}66`, height: HEAD_H }}>
        <div style={{ fontSize: 12, fontWeight: 700, color, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {card.label}
        </div>
        <div style={{ fontSize: 10, color: "#6B7280", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {card.sub}
        </div>
        {line && (
          <div
            title={line.title || line.text}
            style={{
              fontSize: 10, marginTop: 4, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              color: line.ok === true ? "#047857" : line.ok === false ? "#B91C1C" : "#6B7280",
            }}
          >
            {line.ok === true ? "✓ " : line.ok === false ? "✗ " : ""}
            {line.text}
          </div>
        )}
      </div>
      <div style={{ padding: "4px 6px" }}>
        {rows.length === 0 && <div style={{ fontSize: 11, color: "#9CA3AF", padding: "4px", height: ROW_H }}>Nothing here</div>}
        {rows.map((r) => {
          const isSel = selectedRow === r.key;
          const isFocus = focus?.has(r.key);
          return (
            <div
              key={r.key}
              title={r.title || r.text}
              onClick={(e) => { e.stopPropagation(); onRow(card, r); }}
              style={{
                display: "flex", alignItems: "center", gap: 6, height: ROW_H - 2, marginBottom: 2, padding: "0 6px",
                background: isFocus ? "#ECFDF5" : "white", borderRadius: 5, fontSize: 11, color: "#1F2937",
                boxShadow: isSel ? `inset 0 0 0 1.5px ${color}` : isFocus ? `inset 0 0 0 1px ${GREEN}` : "none",
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: 3, background: r.dot, flexShrink: 0 }} />
              {r.star && <Star size={10} style={{ color: "#CA8A04", fill: "#FACC15", flexShrink: 0 }} />}
              <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.text}</span>
              {r.meta && <span style={{ fontSize: 10, color: r.metaColor || "#6B7280", flexShrink: 0 }}>{r.meta}</span>}
            </div>
          );
        })}
        {card.rows.length > MAX_ROWS && (
          <div
            onClick={(e) => { e.stopPropagation(); onToggle(card.id); }}
            style={{ fontSize: 10, color, fontWeight: 600, padding: "0 6px", height: FOOT_H, lineHeight: `${FOOT_H}px` }}
          >
            {expanded ? "Show less" : `+${hidden} more`}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
}

function BandNode({ data }) {
  return (
    <div style={{ width: ROW_MAX_W, fontSize: 13, fontWeight: 700, color: "#374151" }}>
      {data.label} <span style={{ fontSize: 11, fontWeight: 400, color: "#9CA3AF" }}>{data.sub}</span>
    </div>
  );
}

const NODE_TYPES = { card: CardNode, band: BandNode };

// ── Support analysis ──────────────────────────────────────────────────────────

// ── Cards ─────────────────────────────────────────────────────────────────────

function truncate(str, max = 60) {
  if (!str) return "(untitled)";
  return str.length > max ? str.slice(0, max - 1) + "…" : str;
}

// Commercial pages by URL section (and the store), blog posts by topic cluster, and in the
// "with plan" view one card per stored cluster plan. Edges join a cluster to the sections its
// posts link into; they show only for the selection.
function buildCards(data, ax, plans = null) {
  const { pages = [], review_pages = [] } = data;
  const reviewUrls = new Set(plans ? [] : review_pages.map((r) => normUrl(r.url)));
  const planned = new Map();
  (plans || []).forEach((plan) => {
    [plan.pillar, ...(plan.articles || []).flatMap((a) => [a, ...(a.children || [])])].forEach((item) => {
      if (item?.existing_url) planned.set(normUrl(item.existing_url), PLAN_TIER[item.tier] || "page");
    });
  });

  const cards = [];
  const cardOfPage = new Map();
  const sectionOf = (url) => pathOf(url).split("/").filter(Boolean)[0] || "";

  const pageRow = (p) => {
    const s = ax.support.get(String(p.id));
    const review = reviewUrls.has(normUrl(p.url));
    const plan = planned.get(normUrl(p.url));
    return {
      key: `page-${p.id}`,
      text: truncate(p.title || pathOf(p.url)),
      title: `${p.title} · ${pathOf(p.url)}`,
      dot: review ? OLD : LEVEL[s.level].color,
      star: ax.hubIds.has(String(p.id)),
      meta: review ? "old?" : plan ? `update ${plan}` : `${s.linkers.length} post${s.linkers.length === 1 ? "" : "s"}${s.pillarLinks ? " · pillar" : ""}`,
      metaColor: review ? OLD : plan ? PLAN_STROKE : undefined,
      sort: [ax.hubIds.has(String(p.id)) ? 0 : 1, LEVEL_ORDER[s.level]],
      item: { pageType: p.page_type === "product" ? "product" : "page", url: p.url, pageId: p.id, label: p.title },
    };
  };
  const sortRows = (rows) => rows.sort((a, b) => a.sort[0] - b.sort[0] || a.sort[1] - b.sort[1]);
  const pageSub = (list) => {
    const n = (lvl) => list.filter((p) => ax.support.get(String(p.id)).level === lvl).length;
    return `${list.length} page${list.length === 1 ? "" : "s"} · ${n("none")} no support · ${n("strong")} fully supported`;
  };

  const wpPages = pages.filter((p) => p.page_type === "page");
  const bySection = {};
  for (const p of wpPages) (bySection[sectionOf(p.url)] ||= []).push(p);
  const split = wpPages.length > SECTION_MIN_PAGES;
  const sections = new Map();
  for (const p of wpPages) {
    const sec = sectionOf(p.url);
    // Only sections with 2+ pages get their own card; one-offs stay under Pages.
    const id = split && sec && bySection[sec].length > 1 ? `section-${sec}` : "section-pages";
    if (!sections.has(id)) sections.set(id, { sec: id === "section-pages" ? "" : sec, pages: [] });
    sections.get(id).pages.push(p);
  }
  for (const [id, { sec, pages: list }] of sections) {
    let line = { text: "Top-level pages: the homepage is their hub", ok: null };
    if (sec) {
      const hub = list.find((p) => pathOf(p.url) === `/${sec}`);
      line = hub
        ? { text: `Hub page /${sec}`, ok: true }
        : { text: `No hub page at /${sec}`, ok: false, title: `Nothing lives at /${sec}: a hub page there would link to every page in this section` };
    }
    cards.push({ id, kind: "section", band: "commercial", label: sec ? `/${sec}` : "Pages", sub: pageSub(list), line, rows: sortRows(list.map(pageRow)) });
    list.forEach((p) => cardOfPage.set(normUrl(p.url), id));
  }
  const products = pages.filter((p) => p.page_type === "product");
  if (products.length) {
    cards.push({ id: "section-store", kind: "store", band: "commercial", label: "Store", sub: pageSub(products), line: null, rows: sortRows(products.map(pageRow)) });
    products.forEach((p) => cardOfPage.set(normUrl(p.url), "section-store"));
  }

  const edgeCounts = new Map();
  const bump = (src, tgt, n = 1, label) => {
    const k = `${src}|${tgt}`;
    const cur = edgeCounts.get(k) || { src, tgt, n: 0, label };
    cur.n += n;
    edgeCounts.set(k, cur);
  };

  for (const g of ax.groups.values()) {
    const rows = g.posts.map((a) => {
      const targets = ax.postTargets.get(a.wp_post_id) || [];
      targets.forEach((p) => { const c = cardOfPage.get(normUrl(p.url)); if (c) bump(g.id, c); });
      const plan = planned.get(normUrl(a.wp_url));
      const isPillar = g.pillar === a;
      return {
        key: `post-${a.wp_post_id}`,
        text: truncate(a.title),
        title: a.title,
        dot: targets.length ? GREEN : RED,
        star: isPillar,
        meta: plan ? `update ${plan}` : targets.length ? `→ ${targets.length} page${targets.length === 1 ? "" : "s"}` : "no page link",
        metaColor: plan ? PLAN_STROKE : undefined,
        sort: [isPillar ? 0 : 1, targets.length ? 1 : 0],
        item: { pageType: "post", url: a.wp_url, wp_post_id: a.wp_post_id, label: a.title },
      };
    });
    const covered = rows.filter((r) => r.dot === GREEN).length;
    cards.push({
      id: g.id, kind: g.kind, band: "blog", label: g.label,
      sub: `${g.posts.length} post${g.posts.length === 1 ? "" : "s"} · ${covered} link to a commercial page`,
      line: g.pillar
        ? { text: `Pillar: ${g.pillar.title}`, ok: true, title: `Linked from ${g.pillarLinks} posts in this cluster` }
        : { text: "No pillar yet", ok: false, title: `No post here is linked from ${PILLAR_MIN_LINKS}+ posts of its own cluster` },
      rows: sortRows(rows),
      size: g.posts.length,
    });
  }

  let summary = null;
  if (plans) {
    summary = { plans: plans.length, added: 0, updated: 0, links: 0 };
    plans.forEach((plan, i) => {
      const id = `plan-${i}`;
      const items = [plan.pillar, ...(plan.articles || []).flatMap((a) => [a, ...(a.children || [])])].filter(Boolean);
      const rows = items.map((item) => {
        const existing = item.existing_url && (ax.pageByUrl.get(normUrl(item.existing_url)) || ax.artByUrl.get(normUrl(item.existing_url)));
        if (existing) summary.updated += 1;
        else summary.added += 1;
        const g = existing && ax.groupOf.get(normUrl(item.existing_url));
        if (g) bump(id, g.id, 1, "updates");
        return {
          key: `${id}-${item.id}`,
          text: truncate(item.title),
          title: item.title,
          dot: PLAN_STROKE,
          star: item.tier === 2,
          meta: `${existing ? "update" : "new"} ${PLAN_TIER[item.tier] || "page"}`,
          metaColor: PLAN_STROKE,
          sort: [item.tier || 9, 0],
          item: { pageType: "planned", label: item.title, planItem: { ...item, topic: plan.topic } },
        };
      });
      summary.links += (plan.links || []).length;
      const keyCard = plan.key_page && cardOfPage.get(normUrl(plan.key_page));
      if (keyCard) bump(id, keyCard, 1, "key page");
      cards.push({
        id, kind: "plan", band: "plan", label: plan.topic, sub: `${items.length} planned page${items.length === 1 ? "" : "s"}`,
        line: plan.key_page ? { text: `Supports ${pathOf(plan.key_page)}`, ok: !!keyCard } : { text: "No key page", ok: null },
        rows: rows.sort((a, b) => a.sort[0] - b.sort[0]),
      });
    });
  }

  const edges = [...edgeCounts.values()].map(({ src, tgt, n, label }) => {
    const color = src.startsWith("plan-") ? PLAN_STROKE : GREEN;
    return {
      id: `e-${src}-${tgt}`,
      source: src,
      target: tgt,
      label: label || `${n} link${n === 1 ? "" : "s"}`,
      labelStyle: { fontSize: 10, fill: color, fontWeight: 600 },
      labelBgStyle: { fill: "#FFFFFF" },
      markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14, color },
      style: { stroke: color, strokeWidth: Math.min(1 + n / 4, 4), strokeDasharray: src.startsWith("plan-") ? "5 4" : undefined },
    };
  });
  return { cards, edges, summary };
}

// ── Layout ───────────────────────────────────────────────────────────────────

const BANDS = [
  ["plan", "Planned", "cluster plans: dashed links go to the pages they support"],
  ["commercial", "Commercial pages", "by URL section · dot = support from hub, pillar and posts"],
  ["blog", "Blog topic clusters", "★ = pillar · dot = links to a commercial page"],
];

// Shelf packing per band: cards left to right, wrapping at ROW_MAX_W, so the map grows wide
// before it grows tall.
function layoutCards(cards, expanded) {
  const nodes = [];
  let y = 0;
  for (const [band, label, sub] of BANDS) {
    const list = cards.filter((c) => c.band === band);
    if (!list.length) continue;
    if (band === "commercial") list.sort((a, b) => b.rows.length - a.rows.length);
    if (band === "blog") list.sort((a, b) => b.size - a.size);
    nodes.push({ id: `band-${band}`, type: "band", position: { x: 0, y }, data: { label, sub }, selectable: false, draggable: false });
    y += BAND_H;
    let x = 0;
    let rowH = 0;
    for (const card of list) {
      const h = cardHeight(card, expanded.has(card.id));
      if (x > 0 && x + CARD_W > ROW_MAX_W) {
        x = 0;
        y += rowH + GAP;
        rowH = 0;
      }
      nodes.push({ id: card.id, type: "card", position: { x, y }, data: { card } });
      x += CARD_W + GAP;
      rowH = Math.max(rowH, h);
    }
    y += rowH + GAP * 2;
  }
  return nodes;
}

// ── Pages to review (leftover / old copies still live) ─────────────────────────

function ReviewPages({ items }) {
  const [open, setOpen] = useState(true);
  if (!items?.length) return null;
  const path = (u) => { try { return new URL(u).pathname; } catch { return u; } };
  return (
    <div className="px-6 py-3 bg-red-50 border-b border-red-200 shrink-0" data-testid="review-pages">
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-sm font-semibold text-red-800">
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {items.length} page{items.length === 1 ? "" : "s"} look like old versions that are still live
      </button>
      {open && (
        <>
          <p className="text-xs text-red-700 mt-1 ml-6">
            Visitors and Google can still reach these. Old pages split your rankings and can show outdated offers.
          </p>
          <div className="mt-2 ml-6 flex flex-col gap-2 max-h-56 overflow-y-auto pr-2">
            {items.map((r) => (
              <div key={r.url} className="bg-white border border-red-100 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <a href={r.url} target="_blank" rel="noreferrer" className="text-xs font-bold text-zinc-900 hover:underline truncate">
                    {path(r.url)}
                  </a>
                  <ExternalLink size={10} className="text-zinc-400 shrink-0" />
                  {r.linked_from_home && (
                    <span className="text-[9px] font-bold uppercase tracking-wide text-red-700 bg-red-100 rounded px-1.5 py-0.5">linked from homepage</span>
                  )}
                </div>
                <p className="text-[11px] text-zinc-600 mt-0.5">{r.reasons.join(" · ")}</p>
                <p className="text-[11px] text-zinc-900 mt-0.5"><span className="text-zinc-400">Fix →</span> {r.action}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Coverage bar ──────────────────────────────────────────────────────────────

function CoverageBar({ linked, total }) {
  const pct = total === 0 ? 0 : Math.round((linked / total) * 100);
  const color = pct === 0 ? "#DC2626" : pct < 30 ? "#D97706" : "#059669";
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-gray-500">Link coverage</span>
        <span className="text-xs font-semibold" style={{ color }}>
          {linked} / {total} posts
        </span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
}

// ── Panel: Commercial page (page / product) ───────────────────────────────────

// The page's support stack: hub one URL level up, its cluster's pillar, supporting posts.
function SupportCheck({ ok, title, children }) {
  return (
    <div className="flex items-start gap-2">
      {ok ? <CheckCircle2 size={13} className="text-green-500 shrink-0 mt-0.5" /> : <XCircle size={13} className="text-red-400 shrink-0 mt-0.5" />}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-gray-800">{title}</p>
        <p className="text-[11px] text-gray-500 leading-snug">{children}</p>
      </div>
    </div>
  );
}

function SupportBlock({ s }) {
  const cl = s.cluster;
  return (
    <div className="px-4 py-3 border-b border-gray-100 shrink-0 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">What supports this page</span>
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded text-white" style={{ background: LEVEL[s.level].color }}>
          {LEVEL[s.level].label}
        </span>
      </div>
      <SupportCheck ok={s.hub.status !== "missing"} title="Hub page">
        {s.hub.status === "top" && "Top-level page: the homepage is its hub. Make sure the main menu links here."}
        {s.hub.status === "ok" && <>Lives under <b>{s.hub.path}</b>. Check that hub lists and links to this page.</>}
        {s.hub.status === "missing" && <>Nothing lives at <b>{s.hub.path}</b>. Create a hub page there that links to every page in the section.</>}
      </SupportCheck>
      <SupportCheck ok={s.pillarLinks} title="Pillar content">
        {!cl && "No topic cluster matches this page. Write a pillar guide on its topic that links here."}
        {cl && !s.pillar && <>Cluster “{cl.label}” has no pillar yet (no post its siblings link to). Write one that links here.</>}
        {cl && s.pillar && !s.pillarLinks && <>“{s.pillar.title}”, the pillar of “{cl.label}”, doesn’t link here. Add a link from it.</>}
        {s.pillarLinks && <>“{s.pillar.title}” (pillar of “{cl.label}”) links here.</>}
      </SupportCheck>
      <SupportCheck ok={s.linkers.length >= STRONG_POSTS} title="Supporting articles">
        {s.linkers.length} post{s.linkers.length === 1 ? "" : "s"} link here
        {s.linkers.length < STRONG_POSTS ? `, aim for ${STRONG_POSTS}+` : ""}.
        {cl && <> Best-fit cluster: “{cl.label}” ({s.matchedBy === "links" ? "most of its links come from there" : "closest by topic"}).</>}
      </SupportCheck>
    </div>
  );
}

function CommercialPagePanel({ node, data, ax, onClose }) {
  const { url, pageId, pageType } = node.data;
  const page = useMemo(
    () => data.pages.find((p) => String(p.id) === String(pageId)),
    [data.pages, pageId]
  );
  const support = ax?.support.get(String(pageId));

  // Posts that could link: the best-fit cluster's posts first, the rest of the blog if none fits.
  const { linkedArticles, gapArticles } = useMemo(() => {
    if (!page || !support) return { linkedArticles: [], gapArticles: [] };
    const linked = new Set(support.linkers);
    return {
      linkedArticles: support.linkers,
      gapArticles: support.cluster ? support.candidates : data.articles.filter((a) => !linked.has(a)),
    };
  }, [data, page, support]);

  const { color, border } = cfg(pageType);
  const typeLabel = pageType === "product" ? "Product" : "WP Page";

  return (
    <div className="flex flex-col h-full">
      {/* Panel header */}
      <div className="flex items-start justify-between px-4 py-3 border-b border-gray-100 shrink-0">
        <div className="flex-1 min-w-0 pr-2">
          <span
            className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded mb-1"
            style={{ color, background: cfg(pageType).bg, border: `1px solid ${border}` }}
          >
            {typeLabel}
          </span>
          <h2 className="text-sm font-bold text-gray-900 leading-tight truncate" title={page?.title}>
            {page?.title || node.data.label}
          </h2>
          {url && (
            <div className="flex items-center gap-1 mt-0.5">
              <span className="text-[10px] text-gray-400 truncate max-w-[180px]">{url}</span>
              <CopyBtn text={url} />
              <a href={url} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-gray-600 shrink-0">
                <ExternalLink size={10} />
              </a>
            </div>
          )}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0 mt-0.5">
          <X size={16} />
        </button>
      </div>

      {support && <SupportBlock s={support} />}

      {/* Coverage bar */}
      <div className="px-4 py-3 border-b border-gray-100 shrink-0">
        <CoverageBar linked={linkedArticles.length} total={data.articles.length} />
        {linkedArticles.length === 0 && (
          <p className="text-[11px] text-red-600 mt-2 font-medium">
            No blog posts link to this page — AI agents cannot connect it to your content.
          </p>
        )}
      </div>

      {/* AEO score */}
      {page?.aeo_score != null && page?.aeo_breakdown && (
        <div className="px-4 py-3 border-b border-gray-100 shrink-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500">AI citation readiness</span>
            <span
              className="text-xs font-bold tabular-nums px-1.5 py-0.5 rounded"
              style={{ color: gradeColor(page.aeo_grade), background: gradeColor(page.aeo_grade) + "15" }}
            >
              {Math.round(page.aeo_score)} · {page.aeo_grade}
            </span>
          </div>
          <div className="space-y-1">
            {AEO_FACTORS.map((it) => {
              const v = page.aeo_breakdown[it.key] ?? 0;
              return (
                <div key={it.key} className="flex items-center gap-2 text-[10px]">
                  <span className="w-24 text-gray-400 truncate">{it.label}</span>
                  <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${(v / it.max) * 100}%`, backgroundColor: gradeColor(page.aeo_grade) }}
                    />
                  </div>
                  <span className="w-8 text-right tabular-nums text-gray-600">{v}/{it.max}</span>
                </div>
              );
            })}
          </div>
          {citationGaps(page.aeo_breakdown).length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
              <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400">Copywriting to-do</p>
              {citationGaps(page.aeo_breakdown).map((g) => (
                <div key={g.key} className="flex items-start gap-1.5 text-[10px] text-gray-600 leading-snug">
                  <span className="shrink-0 mt-0.5" style={{ color: gradeColor(page.aeo_grade) }}>&bull;</span>
                  <span><b className="text-gray-800">{g.label}:</b> {g.tip}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Scrollable list area */}
      <div className="flex-1 overflow-y-auto">
        {/* Posts that link here */}
        {linkedArticles.length > 0 && (
          <div className="px-4 pt-3 pb-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2">
              Posts linking here ({linkedArticles.length})
            </p>
            <div className="flex flex-col gap-1.5">
              {linkedArticles.map((art) => (
                <div key={art.wp_post_id} className="flex items-start gap-2">
                  <CheckCircle2 size={13} className="text-green-500 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-700 leading-tight line-clamp-2">{art.title}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <CopyBtn text={art.wp_url} />
                    {art.wp_url && (
                      <a href={art.wp_url} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-gray-600">
                        <ExternalLink size={10} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AEO gap: posts that don't link */}
        {gapArticles.length > 0 && (
          <div className="px-4 pt-3 pb-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">
              AEO gap — posts that could link ({gapArticles.length})
            </p>
            <p className="text-[10px] text-gray-400 mb-2">
              {support?.cluster
                ? `Posts in “${support.cluster.label}” that don't link here yet.`
                : "Add a link from these posts to improve AI citation coverage."}
            </p>
            <div className="flex flex-col gap-1.5">
              {gapArticles.map((art) => (
                <div key={art.wp_post_id} className="flex items-start gap-2">
                  <XCircle size={13} className="text-red-400 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-600 leading-tight line-clamp-2">{art.title}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <CopyBtn text={art.wp_url} />
                    {art.wp_url && (
                      <a href={art.wp_url} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-gray-600">
                        <ExternalLink size={10} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Panel: Blog post ──────────────────────────────────────────────────────────

function PostPanel({ node, data, onClose, onApplied }) {
  const { url, wp_post_id } = node.data;
  const article = useMemo(
    () => data.articles.find((a) => a.wp_post_id === wp_post_id),
    [data.articles, wp_post_id]
  );

  const { linkedPages, gapPages } = useMemo(() => {
    if (!article) return { linkedPages: [], gapPages: [] };
    const outboundUrls = new Set(
      data.internal_links.filter((l) => l.source_url === article.wp_url).map((l) => l.target_url)
    );
    return {
      linkedPages: data.pages.filter((p) => outboundUrls.has(p.url)),
      gapPages: data.pages.filter((p) => !outboundUrls.has(p.url)),
    };
  }, [data, article]);

  const [proposal, setProposal] = useState(null);
  const [loadingProposal, setLoadingProposal] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  const fetchProposal = async () => {
    setLoadingProposal(true);
    setProposal(null);
    setApplyResult(null);
    try {
      const res = await getArticleProposal(wp_post_id, 3);
      setProposal(res);
    } catch (e) {
      setApplyResult({ success: false, message: e.response?.data?.detail || e.message });
    } finally {
      setLoadingProposal(false);
    }
  };

  const handleApply = async () => {
    if (!proposal?.edits?.length) return;
    setApplying(true);
    try {
      const r = await applyLinks({
        wp_post_id,
        edits: proposal.edits.map((e) => ({
          target_wp_post_id: e.target_wp_post_id,
          target_url: e.target_url,
          anchor_text: e.anchor_text,
          match_start: e.match_start,
          match_end: e.match_end,
        })),
      });
      setApplyResult(r);
      if (r.success) {
        setProposal(null);
        onApplied?.();
      }
    } catch (e) {
      setApplyResult({ success: false, message: e.response?.data?.detail || e.message });
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-start justify-between px-4 py-3 border-b border-gray-100 shrink-0">
        <div className="flex-1 min-w-0 pr-2">
          <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded mb-1 text-rose-700 bg-rose-50 border border-rose-200">
            Blog Post
          </span>
          <h2 className="text-sm font-bold text-gray-900 leading-tight line-clamp-2" title={article?.title}>
            {article?.title || node.data.label}
          </h2>
          {url && (
            <div className="flex items-center gap-1 mt-0.5">
              <span className="text-[10px] text-gray-400 truncate max-w-[180px]">{url}</span>
              <CopyBtn text={url} />
              <a href={url} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-gray-600 shrink-0">
                <ExternalLink size={10} />
              </a>
            </div>
          )}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0 mt-0.5">
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Commercial pages this post links to */}
        <div className="px-4 pt-3 pb-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2">
            Commercial pages linked ({linkedPages.length})
          </p>
          {linkedPages.length === 0 ? (
            <p className="text-xs text-gray-400">This post doesn't link to any commercial pages yet.</p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {linkedPages.map((p) => (
                <div key={p.id} className="flex items-start gap-2">
                  <CheckCircle2 size={13} className="text-green-500 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-700 leading-tight">{p.title}</p>
                    <p className="text-[10px] text-gray-400 truncate">{p.url}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Commercial pages not yet linked */}
        {gapPages.length > 0 && (
          <div className="px-4 pt-2 pb-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                Missing links ({gapPages.length})
              </p>
              {!proposal && (
                <button
                  onClick={fetchProposal}
                  disabled={loadingProposal}
                  className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold rounded bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-40"
                >
                  <RefreshCw size={9} className={loadingProposal ? "animate-spin" : ""} />
                  {loadingProposal ? "Loading…" : "Suggest Links"}
                </button>
              )}
            </div>
            <p className="text-[10px] text-gray-400 mb-2">
              Add links to these pages to improve AEO coverage.
            </p>

            {/* Proposal preview + apply */}
            {proposal && (
              <div className="mb-3 border border-zinc-200 rounded-lg overflow-hidden">
                <div className="bg-zinc-50 px-3 py-2 flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                    {proposal.edit_count ?? proposal.edits?.length ?? 0} link{(proposal.edit_count ?? proposal.edits?.length) !== 1 ? "s" : ""} ready
                  </span>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => { setProposal(null); setApplyResult(null); }}
                      className="text-[10px] text-zinc-400 hover:text-zinc-600"
                    >
                      Dismiss
                    </button>
                    <button
                      onClick={handleApply}
                      disabled={applying || !proposal.edits?.length}
                      className="px-2.5 py-1 text-[10px] font-bold bg-zinc-900 text-white rounded hover:bg-zinc-800 disabled:opacity-40"
                    >
                      {applying ? "Applying…" : "Apply"}
                    </button>
                  </div>
                </div>
                {proposal.edits?.length > 0 ? (
                  <div className="divide-y divide-zinc-100">
                    {proposal.edits.map((e, i) => (
                      <div key={i} className="px-3 py-2">
                        <div className="flex items-center gap-1.5 mb-1">
                          <code className="bg-zinc-900 text-white text-[9px] px-1 py-0.5 rounded font-bold">{e.anchor_text}</code>
                          <span className="text-[9px] text-zinc-400">→</span>
                          <span className="text-[10px] text-zinc-700 truncate flex-1">{e.target_title}</span>
                        </div>
                        <p className="text-[9px] text-zinc-400 font-mono leading-relaxed line-clamp-2">
                          …{e.snippet_before}<span className="bg-yellow-100 text-zinc-900 font-bold px-0.5">{e.anchor_text}</span>{e.snippet_after}…
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-3 py-2">
                    <p className="text-[10px] text-amber-700 font-medium mb-1.5">
                      None of the target keywords appear in this post's text.
                    </p>
                    {proposal.no_match_hints?.length > 0 && (
                      <div className="flex flex-col gap-2">
                        <p className="text-[9px] text-zinc-400">
                          Add one of these phrases to enable auto-linking, then click "Suggest Links" again:
                        </p>
                        {proposal.no_match_hints.map((hint, i) => (
                          <div key={i} className="border border-zinc-100 rounded px-2 py-1.5">
                            <p className="text-[10px] text-zinc-600 font-medium truncate mb-1">{hint.target_title}</p>
                            <div className="flex flex-wrap gap-1">
                              {hint.tried_phrases.map((phrase, j) => (
                                <code key={j} className="bg-amber-50 border border-amber-200 text-amber-800 text-[9px] px-1 py-0.5 rounded">
                                  {phrase}
                                </code>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {applyResult && (
              <div className={`mb-2 px-3 py-2 text-[10px] rounded-lg border flex items-center gap-1.5 ${applyResult.success ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-600"}`}>
                {applyResult.success
                  ? <><CheckCircle2 size={11} /> Links applied — sitemap refreshed.</>
                  : <><XCircle size={11} /> {applyResult.message}</>
                }
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              {gapPages.map((p) => (
                <div key={p.id} className="flex items-start gap-2">
                  <XCircle size={13} className="text-red-400 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-600 leading-tight">{p.title}</p>
                    <p className="text-[10px] text-gray-400 truncate">{p.url}</p>
                  </div>
                  <CopyBtn text={p.url} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Panel: Category ───────────────────────────────────────────────────────────

function CategoryPanel({ node, data, onClose }) {
  const catName = node.data.catName || node.data.label;

  const catArticles = useMemo(
    () => data.articles.filter((a) => (a.categories || []).includes(catName)),
    [data.articles, catName]
  );

  const { totalLinks, gapPages } = useMemo(() => {
    const sourceUrls = new Set(catArticles.map((a) => a.wp_url));
    const linkedTargets = new Set(
      data.internal_links.filter((l) => sourceUrls.has(l.source_url)).map((l) => l.target_url)
    );
    const gapPages = data.pages.filter((p) => !linkedTargets.has(p.url));
    return { totalLinks: linkedTargets.size, gapPages };
  }, [data, catArticles]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-start justify-between px-4 py-3 border-b border-gray-100 shrink-0">
        <div className="flex-1 min-w-0 pr-2">
          <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded mb-1 text-red-700 bg-red-50 border border-red-200">
            Category
          </span>
          <h2 className="text-sm font-bold text-gray-900">{catName}</h2>
          <p className="text-[11px] text-gray-400">{catArticles.length} posts in this category</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0 mt-0.5">
          <X size={16} />
        </button>
      </div>

      <div className="px-4 py-3 border-b border-gray-100 shrink-0">
        <CoverageBar linked={totalLinks} total={data.pages.length} />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pt-3 pb-4">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2">
          Commercial pages not covered ({gapPages.length})
        </p>
        {gapPages.length === 0 ? (
          <p className="text-xs text-green-600 font-medium">All commercial pages are linked from this category.</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {gapPages.map((p) => (
              <div key={p.id} className="flex items-start gap-2">
                <XCircle size={13} className="text-red-400 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-600 leading-tight">{p.title}</p>
                </div>
                <CopyBtn text={p.url} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Panel: Cluster ────────────────────────────────────────────────────────────

function ClusterPanel({ node, data, onClose }) {
  const clusterLabel = node.data.clusterLabel || node.data.label;

  const clusterArts = useMemo(() => {
    const groupId = node.id;
    return data.articles.filter((a) => `cluster-${a.cluster_id}` === groupId);
  }, [data.articles, node.id]);

  const { totalLinks, gapPages } = useMemo(() => {
    const sourceUrls = new Set(clusterArts.map((a) => a.wp_url));
    const linkedTargets = new Set(
      data.internal_links.filter((l) => sourceUrls.has(l.source_url)).map((l) => l.target_url)
    );
    return { totalLinks: linkedTargets.size, gapPages: data.pages.filter((p) => !linkedTargets.has(p.url)) };
  }, [data, clusterArts]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-start justify-between px-4 py-3 border-b border-gray-100 shrink-0">
        <div className="flex-1 min-w-0 pr-2">
          <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded mb-1 text-sky-700 bg-sky-50 border border-sky-200">
            Topic Cluster
          </span>
          <h2 className="text-sm font-bold text-gray-900">{clusterLabel}</h2>
          <p className="text-[11px] text-gray-400">{clusterArts.length} posts in this cluster</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0 mt-0.5">
          <X size={16} />
        </button>
      </div>
      <div className="px-4 py-3 border-b border-gray-100 shrink-0">
        <CoverageBar linked={totalLinks} total={data.pages.length} />
      </div>
      <div className="flex-1 overflow-y-auto px-4 pt-3 pb-4">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2">
          Commercial pages not covered ({gapPages.length})
        </p>
        {gapPages.length === 0 ? (
          <p className="text-xs text-green-600 font-medium">All commercial pages linked from this cluster.</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {gapPages.map((p) => (
              <div key={p.id} className="flex items-start gap-2">
                <XCircle size={13} className="text-red-400 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-600 leading-tight">{p.title}</p>
                </div>
                <CopyBtn text={p.url} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Panel: Planned page (from a topic cluster plan) ───────────────────────────

function PlanNodePanel({ node, onClose }) {
  const item = node.data.planItem;
  const path = (u) => { try { return new URL(u).pathname; } catch { return u; } };
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-start justify-between px-4 py-3 border-b border-gray-100 shrink-0">
        <div className="flex-1 min-w-0 pr-2">
          <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded mb-1 text-teal-700 bg-teal-50 border border-teal-200">
            {item.existing_url ? "Update" : "New"} {PLAN_TIER[item.tier] || "page"}
          </span>
          <h2 className="text-sm font-bold text-gray-900 leading-tight">{item.title}</h2>
          <p className="text-[11px] text-gray-400">Topic: {item.topic}</p>
          {item.existing_url && (
            <a href={item.existing_url} target="_blank" rel="noreferrer" className="text-[10px] text-teal-700 hover:underline">
              Update {path(item.existing_url)}
            </a>
          )}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0 mt-0.5">
          <X size={16} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 pt-3 pb-4 space-y-3">
        {item.angle && <p className="text-xs text-gray-700">{item.angle}</p>}
        {item.queries?.length > 0 && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">
              Searches it targets ({item.query_count ?? item.queries.length} · {item.impressions ?? 0} impressions)
            </p>
            {item.queries.map((q) => (
              <p key={q.query} className="text-[11px] text-gray-600">{q.query} <span className="text-gray-400">· {q.impressions}</span></p>
            ))}
          </div>
        )}
        {item.sections?.length > 0 && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Sections</p>
            {item.sections.map((sec) => <p key={sec.article_id} className="text-[11px] text-gray-600">• {sec.title}</p>)}
          </div>
        )}
        <Link to="/dashboard/recommendations" className="inline-block text-[11px] font-semibold text-teal-700 hover:underline">
          Full plan in Recommendations →
        </Link>
      </div>
    </div>
  );
}

// ── Node panel dispatcher ─────────────────────────────────────────────────────

function NodePanel({ node, data, ax, onClose, onApplied }) {
  const { pageType } = node.data;
  if (pageType === "planned") {
    return <PlanNodePanel node={node} onClose={onClose} />;
  }
  if (pageType === "page" || pageType === "product") {
    return <CommercialPagePanel node={node} data={data} ax={ax} onClose={onClose} />;
  }
  if (pageType === "post") {
    return <PostPanel node={node} data={data} onClose={onClose} onApplied={onApplied} />;
  }
  if (pageType === "category") {
    return <CategoryPanel node={node} data={data} onClose={onClose} />;
  }
  if (pageType === "cluster_group") {
    return <ClusterPanel node={node} data={data} onClose={onClose} />;
  }
  return null;
}

// ── Migration SQL ─────────────────────────────────────────────────────────────

const MIGRATION_SQL = `-- Run in Supabase SQL editor
CREATE TABLE IF NOT EXISTS public.site_pages (
  id         BIGSERIAL PRIMARY KEY,
  url        TEXT NOT NULL,
  title      TEXT NOT NULL DEFAULT '',
  page_type  TEXT NOT NULL DEFAULT 'page',
  slug       TEXT DEFAULT '',
  parent_url TEXT DEFAULT '',
  org_id     TEXT NOT NULL,
  synced_at  TIMESTAMPTZ,
  UNIQUE (url, org_id)
);
CREATE INDEX IF NOT EXISTS idx_site_pages_org ON site_pages (org_id);
CREATE INDEX IF NOT EXISTS idx_site_pages_type ON site_pages (page_type, org_id);

ALTER TABLE public.site_pages
  ADD COLUMN IF NOT EXISTS aeo_score DOUBLE PRECISION,
  ADD COLUMN IF NOT EXISTS aeo_breakdown JSONB,
  ADD COLUMN IF NOT EXISTS aeo_grade TEXT;

CREATE TABLE IF NOT EXISTS public.internal_links (
  id          BIGSERIAL PRIMARY KEY,
  source_url  TEXT NOT NULL,
  target_url  TEXT NOT NULL,
  org_id      TEXT NOT NULL,
  UNIQUE (source_url, target_url, org_id)
);
CREATE INDEX IF NOT EXISTS idx_internal_links_org ON internal_links (org_id, target_url);

ALTER TABLE public.site_pages ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.internal_links ENABLE ROW LEVEL SECURITY;`;

// ── Legend ────────────────────────────────────────────────────────────────────

const LEGEND = [
  { type: "section",  label: "Pages section" },
  { type: "store",    label: "Store" },
  { type: "cluster",  label: "Topic cluster" },
  { type: "category", label: "Category" },
  { type: "plan",     label: "Plan (with plan view)" },
];

// ── Page ──────────────────────────────────────────────────────────────────────

export default function SitemapPage() {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [loading, setLoading] = useState(true);
  const [crawling, setCrawling] = useState(false);
  const [error, setError] = useState(null);
  // Set when the crawl had no sitemap.xml and fell back to Search Console + homepage links.
  const [warning, setWarning] = useState(null);
  const [needsMigration, setNeedsMigration] = useState(false);
  const [showSql, setShowSql] = useState(false);
  const [data, setData] = useState(null);
  const [clusterLabels, setClusterLabels] = useState(new Map());
  // "now" = the site as crawled; "plan" = with stored topic cluster plans drawn on (before/after).
  const [view, setView] = useState("now");
  const [plans, setPlans] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  // A card (edges to the cards it links with) and optionally one of its rows (opens the panel).
  const [sel, setSel] = useState(null);
  const [layoutKey, setLayoutKey] = useState(0);

  const ax = useMemo(() => (data ? analyzeSupport(data, clusterLabels) : null), [data, clusterLabels]);
  const graph = useMemo(
    () => (ax ? buildCards(data, ax, view === "plan" ? plans : null) : null),
    [data, ax, view, plans],
  );
  const cardById = useMemo(() => new Map((graph?.cards || []).map((c) => [c.id, c])), [graph]);

  const stats = useMemo(() => {
    if (!data || !ax) return null;
    const lv = [...ax.support.values()].map((s) => s.level);
    return {
      articles: data.articles.length,
      pages: data.pages.length,
      links: data.internal_links.length,
      none: lv.filter((l) => l === "none").length,
      strong: lv.filter((l) => l === "strong").length,
      review: data.review_pages || [],
    };
  }, [data, ax]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tree, clustersRes, plansRes] = await Promise.all([
        getSitemapTree(),
        getClusters().catch(() => ({ clusters: [] })),
        getSitemapPlans().catch(() => ({ plans: [] })),
      ]);
      if (tree.needs_migration) {
        setNeedsMigration(true);
        setLoading(false);
        return;
      }
      setClusterLabels(new Map((clustersRes.clusters || []).map((c) => [String(c.cluster_id), c.label])));
      setPlans(plansRes.plans || []);
      setData(tree);
      setSel(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Re-pack when the cards or an expanded card's height change; remount only for a new graph
  // so expanding a card doesn't throw away the viewport.
  useEffect(() => {
    if (!graph) return;
    setNodes(layoutCards(graph.cards, expanded));
  }, [graph, expanded, setNodes]);
  useEffect(() => {
    setLayoutKey((k) => k + 1);
    setExpanded(new Set());
    setSel(null);
  }, [graph]);

  const handleCrawl = async () => {
    setCrawling(true);
    setError(null);
    setWarning(null);
    try {
      const result = await crawlSitemap();
      setWarning(result?.warning || null);
      await load();
    } catch (err) {
      if (err.response?.status === 422) {
        setNeedsMigration(true);
      } else {
        setError(err.response?.data?.detail || err.message);
      }
    } finally {
      setCrawling(false);
    }
  };

  const onRow = useCallback((card, row) => {
    setSel((prev) => (prev?.row?.key === row.key ? { cardId: card.id } : { cardId: card.id, row }));
  }, []);
  const onToggle = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);
  const onNodeClick = useCallback((_, node) => {
    if (node.type !== "card") return;
    setSel((prev) => (prev?.cardId === node.id && !prev.row ? null : { cardId: node.id }));
  }, []);

  // Selecting a commercial page lights the posts that support it; a post lights the pages it
  // links to. Their cards stay lit, everything else dims.
  const focus = useMemo(() => {
    const row = sel?.row;
    if (!row || !ax) return null;
    const { item } = row;
    if (item.pageId != null) {
      const s = ax.support.get(String(item.pageId));
      return new Set((s?.linkers || []).map((a) => `post-${a.wp_post_id}`));
    }
    if (item.pageType === "post") {
      return new Set((ax.postTargets.get(item.wp_post_id) || []).map((p) => `page-${p.id}`));
    }
    return null;
  }, [sel, ax]);

  const litCards = useMemo(() => {
    if (!sel || !graph) return null;
    const lit = new Set([sel.cardId]);
    if (focus) {
      graph.cards.forEach((c) => { if (c.rows.some((r) => focus.has(r.key))) lit.add(c.id); });
    } else {
      graph.edges.forEach((e) => {
        if (e.source === sel.cardId) lit.add(e.target);
        if (e.target === sel.cardId) lit.add(e.source);
      });
    }
    return lit;
  }, [sel, focus, graph]);

  const displayNodes = useMemo(() => nodes.map((n) => (n.type !== "card" ? n : {
    ...n,
    data: {
      card: cardById.get(n.id) || n.data.card,
      expanded: expanded.has(n.id),
      lit: !litCards || litCards.has(n.id),
      selectedCard: sel?.cardId === n.id,
      selectedRow: sel?.cardId === n.id ? sel.row?.key : null,
      focus,
      onRow,
      onToggle,
    },
  })), [nodes, cardById, expanded, litCards, sel, focus, onRow, onToggle]);

  // Edges only for the selection: with every cluster linking into every section, all of them
  // at once is a hairball.
  const displayEdges = useMemo(() => {
    if (!sel || !graph) return [];
    return graph.edges.filter((e) => (e.source === sel.cardId || e.target === sel.cardId) && litCards.has(e.source) && litCards.has(e.target));
  }, [sel, graph, litCards]);

  // The right panel: a row's page/post/plan item, or a cluster/category card's summary.
  const panelNode = useMemo(() => {
    if (!sel) return null;
    if (sel.row) return { id: sel.row.key, data: sel.row.item };
    const card = cardById.get(sel.cardId);
    if (card?.kind === "cluster") return { id: card.id, data: { pageType: "cluster_group", clusterLabel: card.label, label: card.label } };
    if (card?.kind === "category") return { id: card.id, data: { pageType: "category", catName: card.label, label: card.label } };
    return null;
  }, [sel, cardById]);

  const isEmpty = !loading && !needsMigration && data && !graph?.cards.length;
  const summary = graph?.summary;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white shrink-0">
        <div className="flex items-center gap-3">
          <MapIcon size={20} style={{ color: "#018D4C" }} />
          <div>
            <h1 className="text-lg font-bold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Visual Sitemap
            </h1>
            <p className="text-xs text-gray-500">What supports each commercial page: hub page, pillar and blog posts</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {stats && (
            <div className="flex items-center gap-4 text-xs text-gray-500 mr-2">
              <span><b className="text-gray-800">{stats.articles}</b> posts</span>
              <span><b className="text-gray-800">{stats.pages}</b> pages</span>
              <span><b className="text-gray-800">{stats.links}</b> links</span>
              {stats.strong > 0 && <span className="text-emerald-700 font-semibold">{stats.strong} fully supported</span>}
              {stats.none > 0 && <span className="text-red-600 font-semibold">{stats.none} with no support</span>}
            </div>
          )}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-medium" data-testid="sitemap-view-toggle">
            {[["now", "Now"], ["plan", `With plan${plans.length ? ` (${plans.length})` : ""}`]].map(([mode, label]) => (
              <button
                key={mode}
                onClick={() => setView(mode)}
                disabled={loading}
                className={`px-3 py-2 ${view === mode ? "bg-teal-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            onClick={handleCrawl}
            disabled={crawling || loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
            style={{ background: "#018D4C" }}
          >
            <RefreshCw size={14} className={crawling ? "animate-spin" : ""} />
            {crawling ? "Crawling…" : "Crawl Site"}
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 px-6 py-3 bg-red-50 border-b border-red-200 text-red-700 text-sm shrink-0">
          <AlertCircle size={14} />
          {error}
        </div>
      )}

      {warning && (
        <div className="flex items-start gap-2 px-6 py-3 bg-amber-50 border-b border-amber-200 text-amber-800 text-sm shrink-0">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span className="flex-1">{warning}</span>
          <button onClick={() => setWarning(null)} className="text-amber-500 hover:text-amber-800"><X size={14} /></button>
        </div>
      )}

      {stats && view === "now" && <ReviewPages items={stats.review} />}

      {view === "plan" && !loading && (
        <div className="flex items-center gap-2 px-6 py-2.5 bg-teal-50 border-b border-teal-200 text-teal-900 text-sm shrink-0" data-testid="sitemap-plan-summary">
          {plans.length === 0 ? (
            <span>
              No cluster plans yet. Open a topic in{" "}
              <Link to="/dashboard/recommendations" className="font-semibold underline">Recommendations</Link>{" "}
              and click Plan cluster, then come back to see the site after the plan.
            </span>
          ) : summary && (
            <span>
              After {summary.plans} cluster plan{summary.plans === 1 ? "" : "s"}:{" "}
              <b>+{summary.added}</b> new page{summary.added === 1 ? "" : "s"} ·{" "}
              <b>{summary.updated}</b> existing page{summary.updated === 1 ? "" : "s"} to update ·{" "}
              <b>{summary.links}</b> internal link{summary.links === 1 ? "" : "s"} to add
            </span>
          )}
        </div>
      )}

      {/* Migration needed */}
      {needsMigration && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8">
          <AlertCircle size={40} className="text-amber-500" />
          <h2 className="text-lg font-semibold text-gray-800">Database setup required</h2>
          <p className="text-sm text-gray-500 text-center max-w-md">
            The sitemap feature needs two new tables. Run the migration SQL in your Supabase SQL editor, then click "Crawl Site".
          </p>
          <button onClick={() => setShowSql((s) => !s)} className="text-sm underline text-blue-600">
            {showSql ? "Hide SQL" : "Show migration SQL"}
          </button>
          {showSql && (
            <pre className="bg-gray-900 text-green-400 text-xs rounded-lg p-4 max-w-2xl w-full overflow-auto whitespace-pre-wrap">
              {MIGRATION_SQL}
            </pre>
          )}
        </div>
      )}

      {/* Loading */}
      {loading && !needsMigration && (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
          Loading sitemap…
        </div>
      )}

      {/* Empty state */}
      {isEmpty && (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-500">
          <MapIcon size={40} className="opacity-30" />
          <p className="text-sm font-medium">No site structure yet</p>
          <p className="text-xs text-gray-400">Click "Crawl Site" to map your site's pages and products.</p>
        </div>
      )}

      {/* Canvas + panel */}
      {!loading && !needsMigration && nodes.length > 0 && (
        <div className="flex-1 flex flex-row overflow-hidden">
          {/* React Flow canvas */}
          <div className="flex-1 relative min-w-0">
            <ReactFlow
              key={layoutKey}
              nodes={displayNodes}
              edges={displayEdges}
              onNodesChange={onNodesChange}
              nodeTypes={NODE_TYPES}
              onNodeClick={onNodeClick}
              onPaneClick={() => setSel(null)}
              fitView
              // Never zoom out past readable: a big site scrolls instead of shrinking to dots.
              fitViewOptions={{ padding: 0.05, minZoom: 0.45, maxZoom: 1 }}
              minZoom={0.05}
              maxZoom={2}
              proOptions={{ hideAttribution: true }}
            >
              <Background gap={20} color="#E5E7EB" />
              <Controls />
              <MiniMap
                nodeColor={(n) => cfg(n.data?.card?.kind).border}
                maskColor="rgba(255,255,255,0.7)"
                style={{ bottom: 60 }}
              />
            </ReactFlow>

            {/* Legend */}
            <div className="absolute bottom-4 left-4 bg-white border border-gray-200 rounded-lg shadow-sm p-3 flex flex-wrap gap-2 max-w-xs">
              {LEGEND.map(({ type, label }) => (
                <div key={type} className="flex items-center gap-1.5 text-xs text-gray-600">
                  <div
                    style={{
                      width: 10, height: 10, borderRadius: 3,
                      background: cfg(type).bg,
                      border: `1.5px solid ${cfg(type).border}`,
                      flexShrink: 0,
                    }}
                  />
                  {label}
                </div>
              ))}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs w-full mt-1 pt-1 border-t border-gray-100">
                <span className="font-semibold" style={{ color: GREEN }}>●</span>
                <span className="text-gray-400">hub + pillar + {STRONG_POSTS} posts</span>
                <span className="font-semibold" style={{ color: AMBER }}>●</span>
                <span className="text-gray-400">some</span>
                <span className="font-semibold" style={{ color: RED }}>●</span>
                <span className="text-gray-400">none</span>
                <span className="text-gray-400 w-full">★ hub page / pillar post · on posts: green = links a commercial page</span>
              </div>
            </div>

            {/* Click hint */}
            {!sel && (
              <div className="absolute top-3 right-3 bg-white/90 border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-gray-500 shadow-sm">
                Click a page to see what supports it · click a card for its links
              </div>
            )}
          </div>

          {/* Right panel */}
          {panelNode && data && (
            <div
              className="bg-white border-l border-gray-200 flex flex-col overflow-hidden shrink-0"
              style={{ width: 300 }}
            >
              <NodePanel
                node={panelNode}
                data={data}
                ax={ax}
                onClose={() => setSel(null)}
                onApplied={load}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
